<script lang="ts" setup>
import { debounce } from '@antfu/utils';
import type { RangeFilter } from '~/types/Catalog';

const props = defineProps<{ filter: RangeFilter }>();

const range = ref([props.filter.details.limit_min, props.filter.details.limit_max]);
const minValue = ref(String(props.filter.details.values[0]));
const maxValue = ref(String(props.filter.details.values[1]));

watch(
  () => props.filter.details.values,
  () => {
    minValue.value = String(props.filter.details.values[0]);
    maxValue.value = String(props.filter.details.values[1]);

    range.value = [
      minValue.value === '0' ? props.filter.details.limit_min : Number(minValue.value),
      maxValue.value === '0' ? props.filter.details.limit_max : Number(maxValue.value),
    ];
  },
  { immediate: true },
);

watch(
  () => range.value,
  () => {
    minValue.value = String(range.value[0]);
    maxValue.value = String(range.value[1]);
  },
);

watch(
  () => minValue.value,
  () => {
    range.value[0] = minValue.value === '0' ? props.filter.details.limit_min : Number(minValue.value);
  },
);

watch(
  () => maxValue.value,
  () => {
    range.value[1] = maxValue.value === '0' ? props.filter.details.limit_max : Number(maxValue.value);
  },
);

const route = useRoute();

async function handleFilterChange() {
  const filterSlug = `filter[${props.filter.slug}]`;

  if (route.query.page) delete route.query.page;

  await navigateTo({
    path: route.path,
    query: { ...route.query, [filterSlug]: range.value.join(';') },
  });
}

const debouncedFilterChangeHandler = debounce(500, handleFilterChange);
</script>

<template>
  <div class="range-filter">
    <div class="flex justify-between gap-2">
      <InputField
        v-model="minValue"
        :placeholder="minValue"
        class="flex-grow"
        name="from"
        @input="debouncedFilterChangeHandler"
      />

      <InputField
        v-model="maxValue"
        :placeholder="maxValue"
        class="flex-grow"
        name="to"
        @input="debouncedFilterChangeHandler"
      />
    </div>

    <div class="px-3">
      <PrimeSlider
        v-model="range"
        :max="Number(filter.details.limit_max)"
        :min="Number(filter.details.limit_min)"
        :step="filter.step"
        range
        @change="range.sort((a, b) => a - b)"
        @slideend="debouncedFilterChangeHandler"
      />
    </div>
  </div>
</template>

<style lang="scss">
.range-filter {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
</style>
